/**
 * Shared external Chart.js tooltip handler for card-benefits charts.
 *
 * Renders a light-themed pill (white bg, slate-200 border, 8px radius,
 * shadow) positioned near the hovered element. Each chart provides a
 * `buildBody(tooltipModel)` that returns the inner HTML for the body,
 * plus optional width tweaks via `containerStyle`.
 */

const TOOLTIP_ID = 'card-benefits-chart-tooltip';

export function isDarkMode() {
    return document.documentElement.classList.contains('dark');
}

function getOrCreateTooltipEl() {
    let el = document.getElementById(TOOLTIP_ID);
    if (!el) {
        el = document.createElement('div');
        el.id = TOOLTIP_ID;
        el.style.cssText = [
            'position: fixed',
            'pointer-events: none',
            'opacity: 0',
            'transition: opacity 120ms ease',
            'z-index: 50',
            'font-family: Inter, system-ui, sans-serif',
        ].join(';');
        document.body.appendChild(el);

        // Scroll/resize while a tooltip is shown leaves the dark pill
        // pinned to its old viewport coordinates while the bar moves.
        // Hide on those events so the tooltip re-anchors on next hover.
        const hide = () => {
            el.style.opacity = '0';
        };
        window.addEventListener('scroll', hide, true);
        window.addEventListener('resize', hide);
    }
    return el;
}

/**
 * Create an external tooltip handler for Chart.js.
 *
 * @param {Object} options
 * @param {(model: any) => string} options.buildBody - Returns inner HTML for tooltip body.
 * @param {string} [options.containerStyle] - Extra inline style for the pill (e.g. width).
 */
export function createExternalTooltip({ buildBody, containerStyle = '' }) {
    return (context) => {
        const { chart, tooltip } = context;
        const el = getOrCreateTooltipEl();

        if (!tooltip || tooltip.opacity === 0) {
            el.style.opacity = '0';
            return;
        }

        const dark = isDarkMode();
        el.innerHTML = `
            <div style="
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                padding: 12px 16px;
                border-radius: 8px;
                border: 1px solid ${dark ? '#334155' : '#E2E8F0'};
                background: ${dark ? '#1E293B' : '#FFFFFF'};
                box-shadow: 0 1px 2px 0 rgba(0, 0, 0, ${dark ? '0.4' : '0.05'});
                ${containerStyle}
            ">${buildBody(tooltip)}</div>
        `;

        const canvasRect = chart.canvas.getBoundingClientRect();
        const tooltipWidth = el.offsetWidth;
        const tooltipHeight = el.offsetHeight;

        // Viewport-relative positioning (matches position: fixed) so the
        // tooltip stays anchored to the bar even if the page is scrolled.
        let left = canvasRect.left + tooltip.caretX - tooltipWidth / 2;
        const minLeft = 8;
        const maxLeft = window.innerWidth - tooltipWidth - 8;
        left = Math.max(minLeft, Math.min(left, maxLeft));

        const top = canvasRect.top + tooltip.caretY - tooltipHeight - 12;

        el.style.left = `${left}px`;
        el.style.top = `${top}px`;
        el.style.opacity = '1';
    };
}

const moneyFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

export function formatTooltipMoney(value) {
    if (!value && value !== 0) return '$0';
    return '$' + moneyFormatter.format(Math.round(Number(value)));
}

export function tooltipRow({ dot, label, value, indent = false, dim = false }) {
    const indentPx = indent ? 12 : 0;
    const dark = isDarkMode();
    const labelColor = dim ? (dark ? '#94A3B8' : '#64748B') : dark ? '#F1F5F9' : '#1E293B';
    const valueColor = labelColor;
    const dotHtml = dot
        ? `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${dot};margin-right:8px;flex-shrink:0;"></span>`
        : `<span style="display:inline-block;width:8px;margin-right:8px;flex-shrink:0;"></span>`;
    return `
        <div style="
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
            width: 100%;
            padding-left: ${indentPx}px;
            font-size: 14px;
            line-height: 20px;
        ">
            <span style="display:flex;align-items:center;color:${labelColor};font-weight:400;">
                ${dotHtml}${label}
            </span>
            <span style="color:${valueColor};font-weight:${dim ? '400' : '500'};white-space:nowrap;">
                ${value}
            </span>
        </div>
    `;
}

export function tooltipHeader(text) {
    return `
        <div style="
            color: ${isDarkMode() ? '#F1F5F9' : '#0A0A0A'};
            font-size: 14px;
            font-weight: 500;
            line-height: 20px;
        ">${text}</div>
    `;
}
